import React, { createContext, useContext, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUser } from "@/hooks/useUser";
import { useDocument } from "@/hooks/useDocument";
import { useCompassChatHistory } from "@/hooks/useCompassChat";
import { ICompasAIChatAction } from "@/types/compass.types";
import { extractActionsFromHistory } from "./compass-queue.utils";
import { applyHistoryActions, handleSocketMessage, type ApplyDeps } from "./compass-queue.apply";
import { startQueueSocket } from "./compass-queue-socket";
import { getBackendDocumentId } from "@/utils/document-id.utils";

interface CompassSocketContextType {
  taskStatuses: Record<string, string>;
  activeProcessingTask: ICompasAIChatAction | null;
  isConnected: boolean;
}

const CompassSocketContext = createContext<CompassSocketContextType | undefined>(undefined);

function useApplyDeps(
  docId: string,
  onUpdate?: () => void,
  onMessage?: () => void
): {
  deps: ApplyDeps;
  taskStatuses: Record<string, string>;
  activeProcessingTask: ICompasAIChatAction | null;
} {
  const [taskStatuses, setTaskStatuses] = useState<Record<string, string>>({});
  const [activeProcessingTask, setActiveProcessingTask] = useState<ICompasAIChatAction | null>(
    null
  );
  const taskStatusesRef = React.useRef<Record<string, string>>({});
  const queryClient = useQueryClient();
  const callbacksRef = React.useRef({ onUpdate, onMessage });
  const invalidationTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbacksRef.current = { onUpdate, onMessage };
  }, [onUpdate, onMessage]);

  const deps: ApplyDeps = {
    taskStatusesRef,
    invalidationTimeoutRef,
    callbacksRef,
    setTaskStatuses,
    setActiveProcessingTask,
    queryClient,
    docId,
  };

  return { deps, taskStatuses, activeProcessingTask };
}

function useHistoryStatusSync(
  history: ReturnType<typeof useCompassChatHistory>["data"],
  deps: ApplyDeps
): void {
  const depsRef = React.useRef(deps);
  // Committed effect, not a render-body write: a discarded render must not
  // publish speculative deps to the effect below.
  useEffect(() => {
    depsRef.current = deps;
  });
  useEffect(() => {
    applyHistoryActions(extractActionsFromHistory(history), depsRef.current);
  }, [history]);
}

function useQueueSocketConnection(
  enabled: boolean,
  docId: string,
  deps: ApplyDeps,
  setIsConnected: React.Dispatch<React.SetStateAction<boolean>>
): void {
  const depsRef = React.useRef(deps);
  // Committed effect, not a render-body write. Declared first so the retained
  // socket callback below always reads deps from a render that committed.
  useEffect(() => {
    depsRef.current = deps;
  });

  useEffect(() => {
    if (!enabled || docId === "") return;

    const handle = startQueueSocket(docId, {
      onOpen: () => setIsConnected(true),
      onClose: () => setIsConnected(false),
      onMessage: (event) => handleSocketMessage(event, depsRef.current),
      onTerminalClose: () => {
        setIsConnected(false);
        toast.error("Compass updates are unavailable");
      },
      onReconnectExhausted: () => {
        setIsConnected(false);
        toast.error("Compass updates are unavailable. Reopen the document to reconnect.");
      },
    });

    return () => {
      // `dispose` marks the session disposed before it closes the socket, so
      // the close handler returns without calling `onClose`. Reset the flag
      // here or a sign-out or document switch leaves `isConnected` true with
      // no socket behind it.
      handle.dispose();
      setIsConnected(false);
      const current = depsRef.current;
      if (current.invalidationTimeoutRef.current != null) {
        clearTimeout(current.invalidationTimeoutRef.current);
      }
      current.taskStatusesRef.current = {};
      current.setTaskStatuses({});
      current.setActiveProcessingTask(null);
    };
  }, [enabled, docId, setIsConnected]);
}

function useCompassConnection(
  onUpdate?: () => void,
  onMessage?: () => void
): CompassSocketContextType {
  const { user } = useUser();
  const { data: doc } = useDocument();
  const docId = getBackendDocumentId(doc) ?? "";
  const [isConnected, setIsConnected] = useState(false);
  const { deps, taskStatuses, activeProcessingTask } = useApplyDeps(docId, onUpdate, onMessage);
  const { data: history } = useCompassChatHistory(docId);

  useHistoryStatusSync(history, deps);
  useQueueSocketConnection(user != null && docId !== "", docId, deps, setIsConnected);

  return { taskStatuses, activeProcessingTask, isConnected };
}

export const CompassSocketProvider: React.FC<{
  children: React.ReactNode;
  onUpdate?: () => void;
  onMessage?: () => void;
}> = ({ children, onUpdate, onMessage }) => {
  const value = useCompassConnection(onUpdate, onMessage);

  return <CompassSocketContext.Provider value={value}>{children}</CompassSocketContext.Provider>;
};

export const useCompassSocketContext = () => {
  const context = useContext(CompassSocketContext);
  if (context === undefined) {
    throw new Error("useCompassSocketContext must be used within a CompassSocketProvider");
  }
  return context;
};
